/**
 * 可搜索的下拉选择组件
 * 支持输入过滤选项，可自由输入或从选项中选择
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Popover, PopoverAnchor, PopoverContent } from '@leadsnavi/ui-kit';
import { ChevronDown } from 'lucide-react';
import cn from 'classnames';

export interface InputSelectProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
  emptyText?: string;
}

const InputSelect: React.FC<InputSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = 'e.g., Automotive',
  className,
  error = false,
  emptyText = 'No matching options',
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return options;
    const keyword = inputValue.trim().toLowerCase();
    return options.filter(opt => opt.toLowerCase().includes(keyword));
  }, [options, inputValue]);

  const handleSelect = useCallback(
    (option: string) => {
      onChange(option);
      setInputValue(option);
      setOpen(false);
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setInputValue(v);
      onChange(v);
    },
    [onChange]
  );

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
  }, []);

  const handleBlur = useCallback(() => {
    onChange(inputValue);
  }, [inputValue, onChange]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          <Input
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={cn('w-full h-9 pr-9', error && 'border-red-500', className)}
          />
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#404040]"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="flex max-h-[240px] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-[8px] border border-[#D4D4D4] p-0.5 shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.1),0px_4px_6px_-1px_rgba(0,0,0,0.1)]"
        side="bottom"
        align="start"
        sideOffset={4}
        style={{ zIndex: 9999 }}
      >
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col gap-2"
          onWheel={e => e.stopPropagation()}
        >
          {filteredOptions.length === 0 ? (
            <div className="px-2 py-3 text-sm text-[#8E8E8E]">{emptyText}</div>
          ) : (
            filteredOptions.map(option => (
              <button
                key={option}
                type="button"
                className={cn(
                  'flex w-full items-center rounded-lg px-2 py-[5.5px] text-left text-sm text-[#171717] transition hover:bg-[#F7F7F7]',
                  option === value && 'bg-[#F0FFF7]'
                )}
                onMouseDown={e => {
                  e.preventDefault();
                  handleSelect(option);
                }}
              >
                {option}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InputSelect;
